/* Print My Pet – Live-Vorschau (Dummy-Logik, App-Hook-ready)
   Places the uploaded photo on the product mockup inside the print area and
   simulates the chosen art style with canvas filters. The real AI transformation
   happens after checkout (backend/pod-router → AI service). Replace `applyStyle`
   with an API call (window.PMP.previewHook) once an AI endpoint exists. */
(() => {
  'use strict';
  const lp = document.querySelector('[data-live-preview]'); if (!lp) return;
  const canvas = lp.querySelector('canvas'), ctx = canvas.getContext('2d');
  const empty = lp.querySelector('[data-lp-empty]');
  const area = JSON.parse(lp.dataset.printArea || '{"x":0.15,"y":0.12,"w":0.7,"h":0.6,"shape":"rect"}');
  const mock = new Image(); mock.crossOrigin = 'anonymous'; mock.src = lp.dataset.mockup || '';
  let photo = null, style = 'cartoon', zoom = 1, ox = 0, oy = 0;
  const SIZE = 1000; canvas.width = SIZE; canvas.height = SIZE;

  const styles = {
    cartoon: { filter: 'saturate(1.9) contrast(1.35) brightness(1.05)', post: 'posterize' },
    'pixar-3d': { filter: 'saturate(1.5) contrast(1.15) brightness(1.08) blur(0.3px)', post: 'vignette' },
    'line-art': { filter: 'grayscale(1) contrast(2.4) brightness(1.15)', post: 'edges' },
    aquarell: { filter: 'saturate(1.3) contrast(.9) brightness(1.08) blur(1.2px)', post: 'paper' },
    pastell: { filter: 'saturate(.7) brightness(1.15) contrast(.85)', post: 'paper' },
    realistisch: { filter: 'contrast(1.05) saturate(1.05)', post: null }
  };

  // Fixed noise table – re-rolling Math.random() on every redraw made the paper
  // texture flicker while the sliders were being dragged.
  const grain = Float32Array.from({ length: 4096 }, () => (Math.random() - .5) * 18);

  const post = {
    posterize(img) { const d = img.data; for (let i = 0; i < d.length; i += 4) { d[i] = (d[i] >> 5) << 5; d[i + 1] = (d[i + 1] >> 5) << 5; d[i + 2] = (d[i + 2] >> 5) << 5; } return img; },
    vignette(img, w, h) { const d = img.data; const cx = w / 2, cy = h / 2, r = Math.hypot(cx, cy); for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) { const i = (y * w + x) * 4; const v = 1 - Math.pow(Math.hypot(x - cx, y - cy) / r, 2.2) * .45; d[i] *= v; d[i + 1] *= v; d[i + 2] *= v; } return img; },
    edges(img, w, h) { const s = img.data, out = new Uint8ClampedArray(s.length); for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) { const i = (y * w + x) * 4; const g = (px) => s[px]; const gx = -g(i - 4 - w * 4) + g(i + 4 - w * 4) - 2 * g(i - 4) + 2 * g(i + 4) - g(i - 4 + w * 4) + g(i + 4 + w * 4); const gy = -g(i - 4 - w * 4) - 2 * g(i - w * 4) - g(i + 4 - w * 4) + g(i - 4 + w * 4) + 2 * g(i + w * 4) + g(i + 4 + w * 4); const m = 255 - Math.min(255, Math.hypot(gx, gy)); out[i] = out[i + 1] = out[i + 2] = m > 140 ? 255 : m * .5; out[i + 3] = 255; } img.data.set(out); return img; },
    paper(img) {
      const d = img.data;
      for (let i = 0; i < d.length; i += 4) { const n = grain[(i >> 2) % grain.length]; d[i] += n; d[i + 1] += n; d[i + 2] += n; }
      return img;
    }
  };

  const draw = () => {
    ctx.clearRect(0, 0, SIZE, SIZE);
    if (mock.complete && mock.naturalWidth) ctx.drawImage(mock, 0, 0, SIZE, SIZE);
    else { ctx.fillStyle = '#F4F0EA'; ctx.fillRect(0, 0, SIZE, SIZE); }
    if (!photo) { if (empty) empty.hidden = false; return; }
    if (empty) empty.hidden = true;
    const ax = area.x * SIZE, ay = area.y * SIZE, aw = area.w * SIZE, ah = area.h * SIZE;
    // off-screen render of styled photo
    const off = document.createElement('canvas'); off.width = aw; off.height = ah; const oc = off.getContext('2d');
    const s = Math.max(aw / photo.width, ah / photo.height) * zoom;
    const dw = photo.width * s, dh = photo.height * s;
    oc.filter = styles[style]?.filter || 'none';
    oc.drawImage(photo, (aw - dw) / 2 + ox, (ah - dh) / 2 + oy, dw, dh);
    oc.filter = 'none';
    const p = styles[style]?.post; if (p && post[p]) { const id = oc.getImageData(0, 0, aw, ah); oc.putImageData(post[p](id, aw, ah), 0, 0); }
    ctx.save();
    if (area.shape === 'circle') { ctx.beginPath(); ctx.arc(ax + aw / 2, ay + ah / 2, Math.min(aw, ah) / 2, 0, Math.PI * 2); ctx.clip(); }
    else { ctx.beginPath(); ctx.roundRect ? ctx.roundRect(ax, ay, aw, ah, 8) : ctx.rect(ax, ay, aw, ah); ctx.clip(); }
    ctx.drawImage(off, ax, ay);
    // subtle print texture / shadow so it looks "on" the product
    ctx.globalCompositeOperation = 'multiply'; ctx.fillStyle = 'rgba(0,0,0,.06)'; ctx.fillRect(ax, ay, aw, ah);
    ctx.restore();
    lp.dispatchEvent(new CustomEvent('preview:rendered', { bubbles: true }));
  };

  let drawQueued = false;
  const scheduleDraw = () => {
    if (drawQueued) return;
    drawQueued = true;
    requestAnimationFrame(() => { drawQueued = false; draw(); });
  };

  // hooks
  let photoUrl = null;
  document.addEventListener('upload:change', (e) => {
    const f = e.detail.file;
    if (photoUrl) { URL.revokeObjectURL(photoUrl); photoUrl = null; }
    if (!f) { photo = null; draw(); return; }
    const img = new Image();
    photoUrl = URL.createObjectURL(f);
    img.onload = () => { photo = img; zoom = 1; ox = oy = 0; draw(); };
    img.src = photoUrl;
  });
  document.querySelector('[data-style-picker]')?.addEventListener('change', (e) => { style = e.target.closest('.style-chip')?.dataset.style || style; scheduleDraw(); });
  lp.querySelector('[data-lp-zoom]')?.addEventListener('input', (e) => { zoom = +e.target.value; scheduleDraw(); });
  lp.querySelector('[data-lp-x]')?.addEventListener('input', (e) => { ox = +e.target.value; scheduleDraw(); });
  lp.querySelector('[data-lp-y]')?.addEventListener('input', (e) => { oy = +e.target.value; scheduleDraw(); });
  document.addEventListener('variant:change', (e) => { const m = e.detail.variant?.featured_media?.preview_image?.src; if (m && lp.dataset.followVariant === 'true') { mock.src = m; } });
  mock.onload = draw; draw();

  // Public hook for a real AI preview endpoint later:
  // window.PMP.previewHook = async (file, style) => Promise<ImageBitmap|HTMLImageElement>
  window.PMP.livePreview = { redraw: scheduleDraw, setStyle: (s) => { style = s; scheduleDraw(); } };
})();
